/**
 * Action Rendering Types
 *
 * Consolidates types used for rendering actions in the UniversalDetailActions component.
 */

import type { DetailAction, DetailActionContext, BaseEntity } from "./detailPage";
import type { ButtonVariant, ButtonSize } from "@/lib/types/component-patterns";
import {
  INTENT_BUTTON_VARIANT,
  resolveActionIntent,
  type ActionIntent,
} from "@/components/shared/EntityActionBar/action-intent";

/**
 * Configuration for rendering an action button
 */
export interface ActionButtonRenderConfig {
  /** Button variant/style */
  variant: ButtonVariant;
  /** Button size */
  size: ButtonSize;
  /** Whether the button is disabled */
  disabled: boolean;
  /** Whether the button is currently loading */
  isLoading: boolean;
  /** CSS classes to apply */
  className?: string;
  /** Whether this is a dropdown item */
  isDropdown?: boolean;
}

/**
 * Grouped actions for rendering
 */
export interface ActionGroups<T extends BaseEntity> {
  /** Primary actions to show prominently */
  primaryActions: DetailAction<T>[];
  /** Secondary actions to show in dropdown/menu */
  secondaryActions: DetailAction<T>[];
}

/**
 * Action execution state
 */
export interface ActionExecutionState {
  /** Set of action IDs currently loading */
  loadingActions: Set<string>;
  /** Whether the device is mobile */
  isMobile: boolean;
}

/**
 * Edit mode state for inline editing
 */
export interface EditModeState {
  /** Whether inline edit mode is active */
  isEditMode: boolean;
  /** Whether there are pending changes */
  hasPendingChanges: boolean;
  /** Callback to toggle edit mode */
  onToggleEditMode: () => void;
  /** Callback to save pending changes */
  onSavePending: () => Promise<void>;
}

/**
 * Ticket-specific edit state
 */
export interface TicketEditState extends EditModeState {
  /** Whether this is a ticket detail page */
  isTicketDetail: boolean;
}

/**
 * Task-specific edit state
 */
export interface TaskEditState extends EditModeState {
  /** Whether this is a task detail page */
  isTaskDetail: boolean;
}

/**
 * Get the button variant for an action.
 *
 * Delegates to the SHARED intent vocabulary
 * (`components/shared/EntityActionBar/action-intent.ts`) instead of keeping a
 * second id→variant heuristic. Two independent mappings meant "approve" could
 * look one way on a detail page and another in a detail action bar.
 */
export function getActionVariant(
  actionId: string,
  explicitIntent?: ActionIntent
): ButtonVariant {
  return INTENT_BUTTON_VARIANT[resolveActionIntent(actionId, explicitIntent)];
}

/**
 * Mobile detection breakpoint (pixels)
 */
export const MOBILE_BREAKPOINT = 1024;

/**
 * Default max actions to show before using dropdown
 */
export const DEFAULT_MAX_ACTIONS = 3;
